import type { AICreateTaskPayload } from '@/shared/lib/task/task-payload'
import { resolveTaskModelItems, taskConfigStore } from '@/shared/config/task-config-store'
import type { PointRuleConfigData, PointRuleInputConfig, PointRuleItem } from '../model/point-rule-config-store'

type CreditPayload = Pick<AICreateTaskPayload, 'key' | 'data'> & Partial<Pick<AICreateTaskPayload, 'type'>>
type FormulaToken =
  | { type: 'number'; value: number }
  | { type: 'identifier'; value: string }
  | { type: 'operator'; value: '+' | '-' | '*' | '/' }
  | { type: 'paren'; value: '(' | ')' }
  | { type: 'comma'; value: ',' }

const SOURCE_ALIASES: Record<string, string[]> = {
  audio: ['generate_audio'],
  generate_audio: ['audio'],
  multi: ['upscaling_resize'],
  upscaling_resize: ['multi'],
  aspectRatio: ['aspect_ratio', 'ratio'],
  aspect_ratio: ['aspectRatio', 'ratio'],
  ratio: ['aspectRatio', 'aspect_ratio'],
  resolution: ['image_size', 'size'],
  image_size: ['resolution', 'size'],
  size: ['image_size', 'resolution'],
}

const TASK_KEY_ALIASES: Record<string, string[]> = {
  translate: ['translate'],
  text_expand: ['text-llm'],
  text_list: ['text-llm'],
  'text-llm': ['text_expand', 'text_list'],
  qma: ['multi-angle', 'multi_angle', 'multiAngle'],
  'multi-angle': ['qma'],
  i2vi: ['reference', 'image_to_video', 'image-to-video'],
  t2vi: ['text_to_video', 'text-to-video'],
  f2vi: ['start_end_frame', 'start-end-frame'],
  us: ['fus', 'uss'],
  fus: ['us'],
  uss: ['us'],
}

function logPointRuleWarning(_message: string, _extra?: unknown) {
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readString(value: unknown) {
  return typeof value === 'string' ? value.trim() : ''
}

function normalizeNumber(value: unknown) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

function appendUnique(values: string[], value: string) {
  if (value && !values.includes(value)) {
    values.push(value)
  }
}

function canonicalizeLookupValue(value: unknown) {
  return readString(value)
    .toLowerCase()
    .replace(/[._\s]+/g, '-')
    .replace(/-v(?=\d)/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
}

function collectTaskModelItems() {
  const rawModelConfig = taskConfigStore.rawModelConfig

  return [
    ...resolveTaskModelItems(rawModelConfig?.image),
    ...resolveTaskModelItems(rawModelConfig?.video),
    ...resolveTaskModelItems(rawModelConfig?.text),
  ]
}

function resolveModelRefTaskKeyAliases(taskKey: string) {
  const candidates: string[] = []
  const modelRef = taskConfigStore.modelRef

  if (!modelRef || !taskKey) {
    return candidates
  }

  for (const [bucketKey, items] of Object.entries(modelRef)) {
    const normalizedBucketKey = readString(bucketKey)
    if (!normalizedBucketKey || !Array.isArray(items)) {
      continue
    }

    if (normalizedBucketKey === taskKey) {
      for (const item of items) {
        appendUnique(candidates, readString(item.key))
      }
      continue
    }

    if (items.some((item) => readString(item.key) === taskKey)) {
      appendUnique(candidates, normalizedBucketKey)
    }
  }

  return candidates
}

function resolveModelCandidates(modelValue: unknown) {
  const model = readString(modelValue)
  const candidates: string[] = []
  appendUnique(candidates, model)

  for (const item of collectTaskModelItems()) {
    const itemKey = readString(item.key)
    const itemModel = readString(item.model)
    if (itemKey === model || itemModel === model) {
      appendUnique(candidates, itemKey)
      appendUnique(candidates, itemModel)
    }
  }

  return candidates
}

function resolveTaskKeyCandidates(taskKeyValue: unknown) {
  const taskKey = readString(taskKeyValue)
  const candidates: string[] = []
  appendUnique(candidates, taskKey)

  for (const alias of TASK_KEY_ALIASES[taskKey] ?? []) {
    appendUnique(candidates, alias)
  }

  for (const alias of resolveModelRefTaskKeyAliases(taskKey)) {
    appendUnique(candidates, alias)
  }

  return candidates
}

function readPathValue(target: Record<string, unknown>, source: string) {
  const parts = source.split('.')
  let current: unknown = target

  for (const part of parts) {
    if (!isRecord(current) || !Object.prototype.hasOwnProperty.call(current, part)) {
      return undefined
    }
    current = current[part]
  }

  return current
}

function resolvePixelFromDimensions(payloadData: Record<string, unknown>) {
  const width = readDimensionValue(payloadData, 'width')
  const height = readDimensionValue(payloadData, 'height')

  if (width !== null && height !== null && width > 0 && height > 0) {
    return width * height
  }

  const derivedSize = resolveSizeFromImageConfig(payloadData)
  if (!derivedSize) {
    return null
  }

  return derivedSize.width * derivedSize.height
}

function readDimensionValue(payloadData: Record<string, unknown>, dimension: 'width' | 'height') {
  const directCandidates = dimension === 'width'
    ? ['width', 'outputWidth', 'sourceWidth']
    : ['height', 'outputHeight', 'sourceHeight']

  for (const source of directCandidates) {
    const value = normalizeNumber(readPathValue(payloadData, source))
    if (value !== null && value > 0) {
      return value
    }
  }

  const editor = payloadData.editor
  if (isRecord(editor)) {
    const editorCandidates = dimension === 'width'
      ? ['outputWidth', 'sourceWidth']
      : ['outputHeight', 'sourceHeight']

    for (const source of editorCandidates) {
      const value = normalizeNumber(readPathValue(editor, source))
      if (value !== null && value > 0) {
        return value
      }
    }
  }

  return null
}

function parseAspectRatio(value: unknown) {
  const rawValue = readString(value)
  if (!rawValue || rawValue === 'auto') {
    return null
  }

  const matched = rawValue.match(/^(\d+(?:\.\d+)?)\s*[:/]\s*(\d+(?:\.\d+)?)$/)
  if (!matched) {
    return null
  }

  const widthRatio = Number(matched[1])
  const heightRatio = Number(matched[2])

  if (!Number.isFinite(widthRatio) || !Number.isFinite(heightRatio) || widthRatio <= 0 || heightRatio <= 0) {
    return null
  }

  return widthRatio / heightRatio
}

function parseImageSizePair(value: unknown) {
  if (Array.isArray(value) && value.length >= 2) {
    const width = normalizeNumber(value[0])
    const height = normalizeNumber(value[1])
    if (width !== null && height !== null && width > 0 && height > 0) {
      return { width, height }
    }
  }

  if (isRecord(value)) {
    const width = normalizeNumber(value.width)
    const height = normalizeNumber(value.height)
    if (width !== null && height !== null && width > 0 && height > 0) {
      return { width, height }
    }
  }

  const rawValue = readString(value)
  if (!rawValue) {
    return null
  }

  const explicitMatch = rawValue.match(/(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)/)
  if (explicitMatch) {
    const width = Number(explicitMatch[1])
    const height = Number(explicitMatch[2])
    if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
      return { width, height }
    }
  }

  return null
}

function resolveSizeFromImageConfig(payloadData: Record<string, unknown>) {
  const sizeCandidates = [
    readPathValue(payloadData, 'image_size'),
    readPathValue(payloadData, 'resolution'),
    readPathValue(payloadData, 'size'),
  ]

  for (const sizeCandidate of sizeCandidates) {
    const explicitSize = parseImageSizePair(sizeCandidate)
    if (explicitSize) {
      return explicitSize
    }
  }

  const rawSize = sizeCandidates
    .map(readString)
    .find((value) => value && value.toLowerCase() !== 'auto') ?? ''
  const ratio = parseAspectRatio(
    readPathValue(payloadData, 'aspect_ratio')
    ?? readPathValue(payloadData, 'aspectRatio')
    ?? readPathValue(payloadData, 'ratio'),
  )

  const resolutionMatch = rawSize.match(/(\d+(?:\.\d+)?)\s*p$/i)
  if (!resolutionMatch || ratio === null) {
    return null
  }

  const height = Number(resolutionMatch[1])
  if (!Number.isFinite(height) || height <= 0) {
    return null
  }

  const width = Math.round(height * ratio)
  if (width <= 0) {
    return null
  }

  return { width, height }
}

function resolveImageSizeLabel(payloadData: Record<string, unknown>) {
  const rawImageSize = [
    readPathValue(payloadData, 'image_size'),
    readPathValue(payloadData, 'resolution'),
    readPathValue(payloadData, 'size'),
  ]
    .map(readString)
    .find((value) => value && value.toLowerCase() !== 'auto') ?? ''

  if (rawImageSize) {
    return rawImageSize
  }

  const derivedSize = resolveSizeFromImageConfig(payloadData)
  if (!derivedSize) {
    return null
  }

  const maxDimension = Math.max(derivedSize.width, derivedSize.height)
  if (maxDimension <= 1024) {
    return '1K'
  }
  if (maxDimension <= 2048) {
    return '2K'
  }
  return '4K'
}

function readPayloadValue(payloadData: Record<string, unknown>, source: string): unknown {
  if (source === 'pixel') {
    return resolvePixelFromDimensions(payloadData)
  }

  if (source === 'image_size') {
    const imageSize = resolveImageSizeLabel(payloadData)
    if (imageSize) {
      return imageSize
    }
  }

  const candidates = [source, ...(SOURCE_ALIASES[source] ?? [])]
  for (const candidate of candidates) {
    const value = readPathValue(payloadData, candidate)
    if (value !== undefined) {
      return value
    }
  }

  return undefined
}

export function resolvePointRuleItem(
  payload: CreditPayload,
  config: PointRuleConfigData | null,
) {
  const taskKeyCandidates = resolveTaskKeyCandidates(payload.key)
  const matchedTaskBucketEntry = taskKeyCandidates
    .map((candidate) => [candidate, config?.[candidate]] as const)
    .find((entry) => entry[1])
  const canonicalTaskKeyCandidates = taskKeyCandidates
    .map((candidate) => canonicalizeLookupValue(candidate))
    .filter(Boolean)
  const canonicalTaskBucketEntry = !matchedTaskBucketEntry && config
    ? Object.entries(config).find(([bucketKey]) => canonicalTaskKeyCandidates.includes(canonicalizeLookupValue(bucketKey)))
    : undefined

  const taskKey = matchedTaskBucketEntry?.[0] ?? canonicalTaskBucketEntry?.[0] ?? readString(payload.key)
  const taskBucket = matchedTaskBucketEntry?.[1] ?? canonicalTaskBucketEntry?.[1]

  if (!taskBucket) {
    logPointRuleWarning(`missing task bucket for ${taskKey || '<empty>'}`, {
      payload,
      taskKeyCandidates,
    })
    return null
  }

  for (const candidate of resolveModelCandidates(payload.data.model)) {
    if (taskBucket[candidate]) {
      return taskBucket[candidate] ?? null
    }
  }

  const canonicalModelCandidates = resolveModelCandidates(payload.data.model)
    .map((candidate) => canonicalizeLookupValue(candidate))
    .filter(Boolean)

  if (canonicalModelCandidates.length > 0) {
    const canonicalModelEntry = Object.entries(taskBucket).find(([bucketKey]) => {
      return canonicalModelCandidates.includes(canonicalizeLookupValue(bucketKey))
    })

    if (canonicalModelEntry) {
      return canonicalModelEntry[1] ?? null
    }
  }

  if (taskBucket.default) {
    return taskBucket.default
  }

  logPointRuleWarning(`missing model bucket for ${taskKey}`, {
    taskKey,
    model: payload.data.model,
  })
  return null
}

function normalizeLookupKey(value: unknown) {
  if (typeof value === 'boolean') {
    return String(value)
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : ''
  }

  return readString(value)
}

function resolveMappedValue(ruleItem: PointRuleItem, mapName: string, rawValue: unknown) {
  const mapTable = ruleItem[mapName]
  if (!isRecord(mapTable)) {
    return null
  }

  const lookupKey = normalizeLookupKey(rawValue)
  if (!lookupKey) {
    return null
  }

  if (Object.prototype.hasOwnProperty.call(mapTable, lookupKey)) {
    return normalizeNumber(mapTable[lookupKey])
  }

  const caseInsensitiveKey = Object.keys(mapTable).find((key) => (
    key.toLowerCase() === lookupKey.toLowerCase()
  ))
  if (caseInsensitiveKey) {
    return normalizeNumber(mapTable[caseInsensitiveKey])
  }

  return null
}

function resolveMapTableValue(mapTable: unknown, rawValue: unknown) {
  if (!isRecord(mapTable)) {
    return null
  }

  const lookupKey = normalizeLookupKey(rawValue)
  if (!lookupKey) {
    return null
  }

  if (Object.prototype.hasOwnProperty.call(mapTable, lookupKey)) {
    return normalizeNumber(mapTable[lookupKey])
  }

  const caseInsensitiveKey = Object.keys(mapTable).find((key) => (
    key.toLowerCase() === lookupKey.toLowerCase()
  ))
  if (caseInsensitiveKey) {
    return normalizeNumber(mapTable[caseInsensitiveKey])
  }

  return null
}

function readRangeEntryValue(entry: Record<string, unknown>) {
  return normalizeNumber(entry.value ?? entry.point ?? entry.score ?? entry.result)
}

function readRangeInclusive(entry: Record<string, unknown>, side: 'min' | 'max') {
  const includeCandidate = side === 'min'
    ? entry.includeMin ?? entry.include_min ?? entry.minInclusive ?? entry.min_inclusive ?? entry.leftClosed
    : entry.includeMax ?? entry.include_max ?? entry.maxInclusive ?? entry.max_inclusive ?? entry.rightClosed

  return typeof includeCandidate === 'boolean' ? includeCandidate : true
}

function matchesRangeObject(entry: Record<string, unknown>, value: number) {
  const min = normalizeNumber(entry.min ?? entry.from ?? entry.start)
  const max = normalizeNumber(entry.max ?? entry.to ?? entry.end)
  const minInclusive = readRangeInclusive(entry, 'min')
  const maxInclusive = readRangeInclusive(entry, 'max')

  if (min !== null) {
    const minMatched = minInclusive ? value >= min : value > min
    if (!minMatched) {
      return false
    }
  }

  if (max !== null) {
    const maxMatched = maxInclusive ? value <= max : value < max
    if (!maxMatched) {
      return false
    }
  }

  return min !== null || max !== null
}

function matchesRangeExpression(expression: string, value: number) {
  const normalized = expression.replace(/\s+/g, '')

  const closedMatch = normalized.match(/^([[(])(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)(\]|\))$/)
  if (closedMatch) {
    const lower = Number(closedMatch[2])
    const upper = Number(closedMatch[3])
    const lowerMatched = closedMatch[1] === '[' ? value >= lower : value > lower
    const upperMatched = closedMatch[4] === ']' ? value <= upper : value < upper
    return lowerMatched && upperMatched
  }

  const betweenMatch = normalized.match(/^(-?\d+(?:\.\d+)?)(?:-|~)(-?\d+(?:\.\d+)?)$/)
  if (betweenMatch) {
    const lower = Number(betweenMatch[1])
    const upper = Number(betweenMatch[2])
    return value >= lower && value <= upper
  }

  const lteMatch = normalized.match(/^<=(-?\d+(?:\.\d+)?)$/)
  if (lteMatch) return value <= Number(lteMatch[1])
  const ltMatch = normalized.match(/^<(-?\d+(?:\.\d+)?)$/)
  if (ltMatch) return value < Number(ltMatch[1])
  const gteMatch = normalized.match(/^>=(-?\d+(?:\.\d+)?)$/)
  if (gteMatch) return value >= Number(gteMatch[1])
  const gtMatch = normalized.match(/^>(-?\d+(?:\.\d+)?)$/)
  if (gtMatch) return value > Number(gtMatch[1])
  const plusMatch = normalized.match(/^(-?\d+(?:\.\d+)?)\+$/)
  if (plusMatch) return value >= Number(plusMatch[1])

  return false
}

function resolveRangeMapValue(rangeTable: unknown, rawValue: number) {
  if (Array.isArray(rangeTable)) {
    for (const entry of rangeTable) {
      if (!isRecord(entry) || !matchesRangeObject(entry, rawValue)) {
        continue
      }

      const resolved = readRangeEntryValue(entry)
      if (resolved !== null) {
        return resolved
      }
    }
  }

  if (isRecord(rangeTable)) {
    for (const [expression, entryValue] of Object.entries(rangeTable)) {
      if (!matchesRangeExpression(expression, rawValue)) {
        continue
      }

      const resolved = normalizeNumber(entryValue)
      if (resolved !== null) {
        return resolved
      }
    }
  }

  return null
}

function resolveInputValue(
  ruleItem: PointRuleItem,
  inputConfig: PointRuleInputConfig,
  payloadData: Record<string, unknown>,
) {
  if (inputConfig.type === 'constant') {
    return normalizeNumber(inputConfig.value)
  }

  const rawValue = readPayloadValue(payloadData, inputConfig.source)
  if (rawValue === undefined) {
    return null
  }

  if (inputConfig.type === 'number') {
    return normalizeNumber(rawValue)
  }

  if (inputConfig.type === 'step') {
    const numericValue = normalizeNumber(rawValue)
    const step = normalizeNumber(inputConfig.step)
    if (numericValue === null || step === null || step <= 0) {
      return null
    }

    const steppedValue = Math.ceil(numericValue / step)
    return Math.max(inputConfig.min ?? 0, steppedValue)
  }

  if (inputConfig.type === 'map') {
    return resolveMappedValue(ruleItem, inputConfig.map, rawValue)
  }

  if (inputConfig.type === 'range_map') {
    const numericValue = normalizeNumber(rawValue)
    if (numericValue === null) {
      return null
    }

    return resolveRangeMapValue(ruleItem[inputConfig.map], numericValue)
  }

  const selectorValue = readPayloadValue(payloadData, inputConfig.selector_source)
  const selectorKey = normalizeLookupKey(selectorValue)
  if (!selectorKey) {
    return null
  }

  const caseMap = ruleItem[inputConfig.map]
  if (!isRecord(caseMap)) {
    return null
  }

  const selectedKey = Object.prototype.hasOwnProperty.call(caseMap, selectorKey)
    ? selectorKey
    : Object.keys(caseMap).find((key) => key.toLowerCase() === selectorKey.toLowerCase())
  const selectedTable = selectedKey ? caseMap[selectedKey] : caseMap.default

  if (inputConfig.type === 'case_map') {
    return resolveMapTableValue(selectedTable, rawValue)
  }

  const numericValue = normalizeNumber(rawValue)
  if (numericValue === null) {
    return null
  }

  return resolveRangeMapValue(selectedTable, numericValue)
}

export function resolvePointRuleVariables(
  ruleItem: PointRuleItem,
  payloadData: Record<string, unknown>,
) {
  const variables: Record<string, number> = {}

  for (const [variableName, inputConfig] of Object.entries(ruleItem.inputs ?? {})) {
    const value = resolveInputValue(ruleItem, inputConfig, payloadData)
    if (value === null) {
      logPointRuleWarning(`failed to resolve variable ${variableName}`, {
        inputConfig,
        payloadData,
      })
      return null
    }

    variables[variableName] = value
  }

  return variables
}

function tokenizeFormula(formula: string) {
  const tokens: FormulaToken[] = []
  let index = 0

  while (index < formula.length) {
    const char = formula[index]

    if (/\s/.test(char)) {
      index += 1
      continue
    }

    if (/[0-9.]/.test(char)) {
      let endIndex = index + 1
      while (endIndex < formula.length && /[0-9.]/.test(formula[endIndex])) {
        endIndex += 1
      }

      const numberValue = normalizeNumber(formula.slice(index, endIndex))
      if (numberValue === null) {
        return null
      }

      tokens.push({ type: 'number', value: numberValue })
      index = endIndex
      continue
    }

    if (/[A-Za-z_]/.test(char)) {
      let endIndex = index + 1
      while (endIndex < formula.length && /[A-Za-z0-9_]/.test(formula[endIndex])) {
        endIndex += 1
      }

      tokens.push({ type: 'identifier', value: formula.slice(index, endIndex) })
      index = endIndex
      continue
    }

    if (char === '+' || char === '-' || char === '*' || char === '/') {
      tokens.push({ type: 'operator', value: char })
      index += 1
      continue
    }

    if (char === '(' || char === ')') {
      tokens.push({ type: 'paren', value: char })
      index += 1
      continue
    }

    if (char === ',') {
      tokens.push({ type: 'comma', value: char })
      index += 1
      continue
    }

    return null
  }

  return tokens
}

export function evaluatePointFormula(formula: string, variables: Record<string, number>) {
  const tokens = tokenizeFormula(formula)
  if (!tokens || tokens.length === 0) {
    return 0
  }

  let index = 0

  const parseExpression = (): number | null => {
    let value = parseTerm()
    if (value === null) {
      return null
    }

    while (index < tokens.length) {
      const token = tokens[index]
      if (token.type !== 'operator' || (token.value !== '+' && token.value !== '-')) {
        break
      }

      index += 1
      const rightValue = parseTerm()
      if (rightValue === null) {
        return null
      }

      value = token.value === '+' ? value + rightValue : value - rightValue
    }

    return value
  }

  const parseTerm = (): number | null => {
    let value = parseFactor()
    if (value === null) {
      return null
    }

    while (index < tokens.length) {
      const token = tokens[index]
      if (token.type !== 'operator' || (token.value !== '*' && token.value !== '/')) {
        break
      }

      index += 1
      const rightValue = parseFactor()
      if (rightValue === null) {
        return null
      }

      value = token.value === '*' ? value * rightValue : value / rightValue
    }

    return value
  }

  const parseFactor = (): number | null => {
    const token = tokens[index]
    if (!token) {
      return null
    }

    if (token.type === 'operator' && token.value === '-') {
      index += 1
      const nestedValue = parseFactor()
      return nestedValue === null ? null : -nestedValue
    }

    if (token.type === 'number') {
      index += 1
      return token.value
    }

    if (token.type === 'identifier') {
      index += 1

      if (tokens[index]?.type === 'paren' && tokens[index].value === '(') {
        return parseFunctionCall(token.value)
      }

      return normalizeNumber(variables[token.value])
    }

    if (token.type === 'paren' && token.value === '(') {
      index += 1
      const nestedValue = parseExpression()
      const closingToken = tokens[index]
      if (nestedValue === null || closingToken?.type !== 'paren' || closingToken.value !== ')') {
        return null
      }

      index += 1
      return nestedValue
    }

    return null
  }

  const parseFunctionCall = (functionName: string): number | null => {
    const normalizedFunctionName = functionName.toLowerCase()
    const openingToken = tokens[index]
    if (openingToken?.type !== 'paren' || openingToken.value !== '(') {
      return null
    }

    index += 1
    const args: number[] = []

    if (tokens[index]?.type === 'paren' && tokens[index].value === ')') {
      return null
    }

    while (index < tokens.length) {
      const value = parseExpression()
      if (value === null) {
        return null
      }
      args.push(value)

      const separator = tokens[index]
      if (separator?.type === 'comma') {
        index += 1
        continue
      }

      if (separator?.type === 'paren' && separator.value === ')') {
        index += 1
        break
      }

      return null
    }

    if (args.length === 0) {
      return null
    }

    if (normalizedFunctionName === 'max') {
      return Math.max(...args)
    }

    return null
  }

  const result = parseExpression()
  if (result === null || index !== tokens.length || !Number.isFinite(result)) {
    logPointRuleWarning(`failed to evaluate formula: ${formula}`, { variables })
    return 0
  }

  return Math.max(0, result)
}

export function calculatePointConsume(options: {
  payload: CreditPayload
  config: PointRuleConfigData | null
}) {
  const ruleItem = resolvePointRuleItem(options.payload, options.config)
  if (!ruleItem) {
    return 0
  }

  const variables = resolvePointRuleVariables(ruleItem, options.payload.data)
  if (!variables) {
    return 0
  }

  return Math.ceil(evaluatePointFormula(ruleItem.formula, variables))
}
